import React from 'react';
import { ThemeProvider, createTheme, CssBaseline, Theme } from '@mui/material';

type Mode = 'light' | 'dark';

interface ThemeModeContextType {
  mode: Mode;
  toggleMode: () => void;
  resetTheme: () => void;
  theme: Theme;
}

const ThemeModeContext = React.createContext<ThemeModeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeModeContextType => {
  const ctx = React.useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
};

const getInitialMode = (): Mode => {
  try {
    const stored = localStorage.getItem('coresupport_theme_mode');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = React.useState<Mode>(getInitialMode);
  
  const toggleMode = React.useCallback(() => {
    setMode((m) => (m === 'light' ? 'dark' : 'light'));
  }, []);

  const resetTheme = React.useCallback(() => {
    try {
      localStorage.removeItem('coresupport_theme_mode');
    } catch {}
    setMode('light');
  }, []);

  React.useEffect(() => {
    try { localStorage.setItem('coresupport_theme_mode', mode); } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  const theme = React.useMemo(() => {
    const isDark = mode === 'dark';
    
    // Enterprise Palette
    const palette = {
      mode,
      primary: {
        main: '#4F46E5', // Indigo 600
        light: '#818CF8', // Indigo 400
        dark: '#3730A3', // Indigo 800
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#EC4899', // Pink 500
        light: '#F472B6', // Pink 400
        dark: '#DB2777', // Pink 600
        contrastText: '#ffffff',
      },
      success: {
        main: '#10B981', // Emerald 500
        light: '#34D399',
        dark: '#059669',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#F59E0B', // Amber 500
        light: '#FBBF24',
        dark: '#D97706',
        contrastText: '#ffffff',
      },
      error: {
        main: '#EF4444', // Red 500
        light: '#F87171',
        dark: '#B91C1C',
        contrastText: '#ffffff',
      },
      info: {
        main: '#3B82F6', // Blue 500
        light: '#60A5FA',
        dark: '#2563EB',
        contrastText: '#ffffff',
      },
      background: {
        default: isDark ? '#0F172A' : '#F8FAFC', // Slate 900 : Slate 50
        paper: isDark ? '#1E293B' : '#FFFFFF', // Slate 800 : White
      },
      text: {
        primary: isDark ? '#F8FAFC' : '#0F172A', // Slate 50 : Slate 900
        secondary: isDark ? '#94A3B8' : '#64748B', // Slate 400 : Slate 500
      },
      divider: isDark ? 'rgba(148, 163, 184, 0.1)' : '#E2E8F0',
    };

    return createTheme({
      palette,
      typography: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        h1: { fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.2 },
        h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
        h3: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
        h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.2 },
        h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.4 },
        h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.4 },
        subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
        subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.57 },
        body1: { fontSize: '1rem', lineHeight: 1.6 },
        body2: { fontSize: '0.875rem', lineHeight: 1.57 },
        button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
      },
      shape: { borderRadius: 12 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: isDark ? '#0F172A' : '#F8FAFC',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '8px', height: '8px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? '#334155' : '#CBD5E1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: isDark ? '#475569' : '#94A3B8',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              borderRadius: 16,
            },
            elevation1: {
              boxShadow: isDark 
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 10,
              padding: '8px 20px',
              boxShadow: 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: isDark 
                  ? '0px 4px 12px rgba(0, 0, 0, 0.4)'
                  : '0px 4px 12px rgba(79, 70, 229, 0.2)',
                transform: 'translateY(-1px)',
              },
            },
            containedPrimary: {
              background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)',
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              backgroundImage: 'none',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 232, 240, 0.8)'}`,
              boxShadow: isDark 
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: 12,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF',
                '& fieldset': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4F46E5',
                  boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)',
                },
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              fontWeight: 600,
            },
          },
        },
      },
    });
  }, [mode]);

  const value = React.useMemo(() => ({ mode, toggleMode, resetTheme, theme }), [mode, toggleMode, resetTheme, theme]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};


